import { TableComponent as DataTable } from 'components/TableComponent'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import { useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { Card, CardBody, CardText, UncontrolledTooltip } from 'reactstrap'
import { useSnackbar } from 'notistack'
import {
  BUSINESS_MYAPPROVAL,
  BUSINESS_TRANSACTION_PURCHASE,
  BUSINESS_TRANSACTION_SELL,
  MANAGE,
  READ,
} from 'utils/subjectActions'
import SidebarDashboard from '../SidebarDashboard'
import BtnMakeTransaction from './BtnMakeTransaction'

import ICTunai from 'assets/icons/currency-dollar.svg'
import ICUangBelanja from 'assets/icons/uang-belanja-wallet.png'
import ICTalangin from 'assets/icons/uang-usaha.png'
import { P } from 'components'
import CustomButton from 'components/Buttons/CustomButton'
import SvgIcon from 'components/Icon/SvgIcon'
import LihatDetail from 'components/Icons/LihatDetail'
import PMBPagination from 'components/PMBPagination'
import ProfilePic from 'components/PMBProfilePic'
import PMBTooltip from 'components/PMBTooltip'
import { AbilityContext } from 'context/Acl'
import { formatNumber } from 'helpers'
import moment from 'moment-timezone'
import { ChevronRight, ChevronUp, User, Info, Archive, RotateCcw } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import colors from 'Theme/colors'
import { formatDatePMB, formatDatePMBV2 } from 'utils/formatDate'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_DETAIL_TRANSACTION } from 'utils/pathUrl'
import '../dashboard.scss'
import {
  useLazyFetchTransactionQuery,
  useLazyFetchTransactionSummaryQuery,
  useArchiveTransactionMutation,
} from '../dashboardApiSlice'
import {
  setDataTrx,
  setFetchOption,
  setIsCustomDate,
  setPaginationTag,
} from '../dashboardSlice'
import HeaderTable from './HeaderTable'
import ModalArchiveTransaction from './ModalArchiveTransaction'
import { COLOR_MAP } from 'config/templateConfig'

const TableDashboard = (props) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const ability = useContext(AbilityContext)
  const [getTrx] = useLazyFetchTransactionQuery()
  const [getTrxSummary] = useLazyFetchTransactionSummaryQuery()
  const [isLoading, setIsLoading] = useState(false)
  const hideArchive = Boolean(props?.hideArchive || props?.projectId)

  const {
    isOpenSidebarFilter,
    tag,
    tagPaginationManual,
    ownerData,
    isCustomDate,
    dataTrx,
  } = useSelector((s) => s.dashboard)

  const isRowArchived = useCallback(
    (row) => {
      const currentType = tag?.type || 'BUY'
      if (currentType === 'BUY') {
        return Boolean(Number(row?.buyerArchived) === 1 || row?.buyerArchived === true)
      }
      return Boolean(Number(row?.sellerArchived) === 1 || row?.sellerArchived === true)
    },
    [tag?.type],
  )


  const normalizeSortDirection = (direction) =>
    String(direction || '').toLowerCase() === 'asc' ? 'asc' : 'desc'

  const getSortableValue = (row, sortField) => {
    switch (sortField) {
      case 'createdAt': {
        const time = new Date(
          row?.trxDate || row?.createdAt || row?.updatedAt || 0,
        ).getTime()
        return Number.isFinite(time) ? time : 0
      }
      case 'updatedAt': {
        const time = new Date(row?.updatedAt || 0).getTime()
        return Number.isFinite(time) ? time : 0
      }
      case 'totalAmount':
        return Number(row?.totalAmount || 0)
      case 'totalItemDetail':
        return Number(row?.totalItemDetail || 0)
      case 'status':
        return String(row?.status || '')
      case 'projectName': {
        if (Array.isArray(row?.projects) && row.projects.length > 0) {
          return row.projects
            .map((p) => p?.projectLabel || p?.label || p?.projectName || '')
            .filter(Boolean)
            .join(', ')
        }
        return String(row?.projectName || '')
      }
      case 'transactionNumber':
        return String(
          row?.orderDetail?.transaction_number ||
          row?.orderDetail?.poNumber ||
          '',
        )
      case 'counterparty': {
        const isBuy = tag?.type === 'BUY'
        const detail = isBuy ? row?.sellerDetail : row?.buyerDetail
        const picName =
          detail?.picName ||
          (isBuy ? row?.picSellerDetail?.name : row?.picBuyerDetail?.name)
        const mainName = detail?.name || detail?.fullName
        return String(picName || mainName || '')
      }
      default: {
        const value = row?.[sortField]
        if (typeof value === 'number') return value
        if (typeof value === 'string') return value
        if (value instanceof Date) return value.getTime()
        return value ?? ''
      }
    }
  }

  const compareSortableValue = (a, b) => {
    const isNumberA = typeof a === 'number' && Number.isFinite(a)
    const isNumberB = typeof b === 'number' && Number.isFinite(b)

    if (isNumberA || isNumberB) {
      return Number(a || 0) - Number(b || 0)
    }

    const textA = String(a ?? '')
    const textB = String(b ?? '')

    return textA.localeCompare(textB, 'id', {
      numeric: true,
      sensitivity: 'base',
    })
  }

  const paginatedData = useMemo(() => {
    // Inject "Expired" status if needed
    const rawItems = dataTrx?.items || []

    const items = rawItems.map((item) => {
      if (
        (item?.escrowpayment?.expiredDate &&
          item?.escrowpayment?.status === 'NEED_CONFIRM_BY_SELLER' &&
          moment(item.escrowpayment.expiredDate).isBefore(moment())) ||
        item?.status?.toLowerCase() === 'expired'
      ) {
        return { ...item, status: 'Expired' }
      }
      return item
    })

    const totalItems = dataTrx?.totalItems ?? items.length
    const size = tagPaginationManual?.size || 10
    const totalPages = dataTrx?.totalPages ?? (Math.ceil(totalItems / size) || 1)
    const currentPage = tagPaginationManual?.page || 0

    return {
      items,
      totalItems,
      totalPages,
      currentPage,
      allItems: {
        items,
        totalItems,
        totalPages,
        currentPage,
      },
    }
  }, [dataTrx, tagPaginationManual?.page, tagPaginationManual?.size])

  const paginatedItems = paginatedData.items
  const totalItemsPaginated = paginatedData.totalItems
  const allItems = paginatedData.allItems
  const totalPages = paginatedData.totalPages
  const currentPage = paginatedData.currentPage

  const getDataTrx = async () => {
    setIsLoading(true)

    const contactUserId =
      tagPaginationManual?.selectedContact?.userId ??
      tagPaginationManual?.selectedContact?.value

    const picUserId =
      tagPaginationManual?.filterPicUserId !== undefined
        ? tagPaginationManual.filterPicUserId
        : tag?.filterPicUserId

    const isBeranda = !props?.isList

    const rawStatus =
      tagPaginationManual?.status !== undefined
        ? tagPaginationManual.status === 'Semua' ? undefined : tagPaginationManual.status
        : tag?.status !== undefined && tag?.status !== 'Semua' ? tag.status : undefined

    // Di beranda, jika tidak ada status yg dipilih, default ke 'aktif'
    const rawStatusParam = rawStatus
      ? String(rawStatus).toLowerCase()
      : isBeranda
        ? 'aktif'
        : undefined
    const statusParam =
      hideArchive && rawStatusParam === 'archive'
        ? undefined
        : rawStatusParam
    const paymentParam = tagPaginationManual?.payment ? String(tagPaginationManual.payment).toLowerCase() : undefined
    const trxTypeParam = tagPaginationManual?.trxType ? String(tagPaginationManual.trxType).toLowerCase() : undefined

    const filtertypeParam = (tag?.type || 'BUY') === 'SELL' ? 'buyid' : 'sellerid'

    const params = {
      type: tag?.type || 'BUY',
      page: tagPaginationManual?.page || 0,
      size: tagPaginationManual?.size || 10,
      s: tagPaginationManual?.searchTerm || undefined,
      status: statusParam,
      payment: paymentParam,
      trxType: trxTypeParam,
      projectId: props?.projectId || tagPaginationManual?.projectId || undefined,
      filterPicUserId: picUserId !== undefined ? picUserId : undefined,
      filtertype: contactUserId ? filtertypeParam : undefined,
      filtertypevalue: contactUserId ? contactUserId : undefined,
      from: tag?.from || undefined,
      to: tag?.to || undefined,
      sortby: tagPaginationManual?.sortby || 'createdAt',
      sortdir: normalizeSortDirection(tagPaginationManual?.sortdir || 'desc'),
    }

    const filterKey = JSON.stringify({
      type: tag?.type || 'BUY',
      s: tagPaginationManual?.searchTerm || undefined,
      status: statusParam,
      payment: paymentParam,
      trxType: trxTypeParam,
      projectId: props?.projectId || tagPaginationManual?.projectId || undefined,
      filterPicUserId: picUserId !== undefined ? picUserId : undefined,
      filtertype: contactUserId ? filtertypeParam : undefined,
      filtertypevalue: contactUserId ? contactUserId : undefined,
      from: tag?.from || undefined,
      to: tag?.to || undefined,
    })

    const shouldFetchSummary =
      isRefetch || prevSummaryFilterKey.current !== filterKey

    const fetchSummaryPromise = shouldFetchSummary
      ? getTrxSummary(params)
      : Promise.resolve(null)

    const [response, summaryResponse] = await Promise.all([
      getTrx(params),
      fetchSummaryPromise,
    ])
    const data = response?.data
    const summaryData = summaryResponse?.data

    if (summaryData) {
      setSummary(summaryData)
      prevSummaryFilterKey.current = filterKey
    }

    if (isRefetch) {
      setIsRefetch(false)
    }

    if (data) {
      const currentType = tag?.type || 'BUY'
      const newTopId = data.items?.[0]?.id
      const prevTopId = lastTopIds.current[currentType]
      const currentTotal = data.totalItems ?? 0
      const prevTotal = prevTotalItems.current[currentType]

      const currentPage = tagPaginationManual?.page || 0
      const currentSortby = tagPaginationManual?.sortby || 'createdAt'
      const currentSortdir = normalizeSortDirection(
        tagPaginationManual?.sortdir || 'desc',
      )

      const isSameFilter = prevFilterKey.current === filterKey
      const isDefaultSort =
        currentSortby === 'createdAt' && currentSortdir === 'desc'
      const isFirstPage = currentPage === 0

      const isInitialMount = !hasLoadedInitialData.current
      const isAbilitiesChanged =
        prevAbilities.current.buy !== abilityBuy ||
        prevAbilities.current.sell !== abilitySell

      // Snackbar transaksi baru di-hide sementara
      const SHOW_NEW_TRX_SNACKBAR = false

      if (
        SHOW_NEW_TRX_SNACKBAR &&
        !isInitialMount &&
        !isAbilitiesChanged &&
        isSameFilter &&
        isFirstPage &&
        isDefaultSort &&
        prevTopId &&
        newTopId &&
        prevTopId !== newTopId &&
        (prevTotal === null || currentTotal >= prevTotal)
      ) {
        enqueueSnackbar('Terdapat transaksi baru tersedia', {
          variant: 'success',
          autoHideDuration: 3000,
        })
      }

      hasLoadedInitialData.current = true
      prevAbilities.current = { buy: abilityBuy, sell: abilitySell }
      lastTopIds.current[currentType] = newTopId
      prevTotalItems.current[currentType] = currentTotal
      prevFilterKey.current = filterKey

      dispatch(setDataTrx(data))
    } else {
      prevFilterKey.current = filterKey
      dispatch(
        setDataTrx({
          items: [],
          totalItems: 0,
          totalPages: 1,
        }),
      )
    }

    setIsLoading(false)
  }

  const abilityBuy = useAbilityAccess({
    action: READ,
    subject: BUSINESS_TRANSACTION_PURCHASE,
  })

  const abilitySell = useAbilityAccess({
    action: READ,
    subject: BUSINESS_TRANSACTION_SELL,
  })

  const [isOpen, setIsOpen] = useState(false)
  const [isOpenSidebar, setIsOpenSidebar] = useState(false)
  const [selectedRow, setSelectedRow] = useState({})
  const [isRefetch, setIsRefetch] = useState(false)
  const [summary, setSummary] = useState({})
  const { enqueueSnackbar } = useSnackbar()
  const [archiveTrx, { isLoading: isLoadingArchive }] =
    useArchiveTransactionMutation()
  const [isModalArchiveOpen, setIsModalArchiveOpen] = useState(false)
  const [modalArchiveData, setModalArchiveData] = useState({
    rows: [],
    isArchive: true,
  })
  const [archiveErrMsg, setArchiveErrMsg] = useState(null)
  const [selectedTableRows, setSelectedTableRows] = useState([])
  const [clearSelectedRows, setClearSelectedRows] = useState(false)

  const canRowBeArchived = (row) => {
    const isArchived = isRowArchived(row)
    const isCompleted = String(row?.status || '').toLowerCase() === 'selesai'
    return !isArchived && isCompleted
  }

  const handleOpenArchiveModal = (rows, isArchive = true) => {
    const rowList = Array.isArray(rows) ? rows : [rows]
    setModalArchiveData({ rows: rowList, isArchive })
    setArchiveErrMsg(null)
    setIsModalArchiveOpen(true)
  }

  const handleConfirmArchive = async () => {
    const { rows, isArchive } = modalArchiveData
    if (!rows || rows.length === 0) return

    const alltradeIds = []
    const escrowpaymentIds = []
    const tradeshortcutIds = []

    rows.forEach((row) => {
      const alltradeId = row?.alltradeId || row?.allTradeId
      const escrowId = row?.escrowpaymentId || row?.escrowPaymentId
      const shortcutId = row?.tradeshortcutId || row?.tradeShortcutId

      if (alltradeId) {
        alltradeIds.push(Number(alltradeId))
      } else if (escrowId) {
        escrowpaymentIds.push(Number(escrowId))
      } else if (shortcutId) {
        tradeshortcutIds.push(Number(shortcutId))
      } else if (row?.id) {
        alltradeIds.push(Number(row.id))
      }
    })

    const payload = {
      archive: isArchive ? 1 : 0,
    }

    if (alltradeIds.length > 0) {
      payload.alltradeIds = alltradeIds
    } else if (escrowpaymentIds.length > 0) {
      payload.escrowpaymentIds = escrowpaymentIds
    } else if (tradeshortcutIds.length > 0) {
      payload.tradeshortcutIds = tradeshortcutIds
    }

    try {
      await archiveTrx(payload).unwrap()
      enqueueSnackbar(
        isArchive
          ? 'Transaksi berhasil diarsipkan'
          : 'Transaksi berhasil dikembalikan dari arsip',
        { variant: 'success', autoHideDuration: 3000 },
      )
      setIsModalArchiveOpen(false)
      setSelectedTableRows([])
      setClearSelectedRows((prev) => !prev)
      setIsRefetch(true)
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.data?.meta?.message ||
        'Gagal memproses transaksi. Silakan coba lagi.'
      setArchiveErrMsg(msg)
    }
  }

  const hasLoadedInitialData = useRef(false)
  const prevAbilities = useRef({ buy: undefined, sell: undefined })
  const lastTopIds = useRef({ BUY: null, SELL: null })
  const prevTotalItems = useRef({ BUY: null, SELL: null })
  const prevFilterKey = useRef(null)
  const prevSummaryFilterKey = useRef(null)

  const handleCallback = () => {
    setIsRefetch(true)
  }
  const handleFilter = (data = {}) => {
    const hasDateFilter =
      Object.prototype.hasOwnProperty.call(data, 'from') ||
      Object.prototype.hasOwnProperty.call(data, 'to')

    if (hasDateFilter) {
      dispatch(
        setFetchOption({
          from: data.from ?? tag?.from,
          to: data.to ?? tag?.to,
          page: 0,
        }),
      )
      dispatch(
        setPaginationTag({
          page: 0,
        }),
      )
      dispatch(setIsCustomDate(true))
      return
    }

    const payload = { page: 0 }

    if (Object.prototype.hasOwnProperty.call(data, 'status')) {
      payload.status = data.status
    }
    if (Object.prototype.hasOwnProperty.call(data, 'payment')) {
      payload.payment = data.payment
    }
    if (Object.prototype.hasOwnProperty.call(data, 'trxType')) {
      payload.trxType = data.trxType
    }
    if (Object.prototype.hasOwnProperty.call(data, 'projectName')) {
      payload.projectName = data.projectName
      payload.projectId = data.projectId
    }
    if (Object.prototype.hasOwnProperty.call(data, 'selectedContact')) {
      payload.selectedContact = data.selectedContact
    }
    if (Object.prototype.hasOwnProperty.call(data, 'filterPicUserId')) {
      payload.filterPicUserId = data.filterPicUserId
      dispatch(
        setFetchOption({
          filterPicUserId: data.filterPicUserId,
          page: 0,
        }),
      )
      // Don't dispatch setPaginationTag + setIsRefetch below,
      // because setFetchOption already changes `tag` which triggers useEffect
      return
    }

    dispatch(setPaginationTag(payload))
  }

  const toggle = () => {
    setIsOpen(!isOpen)
  }

  const toggleSidebar = () => {
    setIsOpenSidebar(!isOpenSidebar)
  }

  const handleSort = (column, direction) => {
    dispatch(
      setPaginationTag({
        sortby: column?.sortField || 'createdAt',
        sortdir: normalizeSortDirection(direction),
        page: 0,
      }),
    )
  }

  const renderProjectBadges = (row) => {
    const projectList = []
    if (Array.isArray(row?.projects) && row.projects.length > 0) {
      const seen = new Set()
      row.projects.forEach((p) => {
        const id = p?.projectId ?? p?.id
        const label = p?.projectLabel ?? p?.label ?? p?.projectName
        const key = id !== undefined && id !== null ? String(id) : label
        if (key && !seen.has(key)) {
          seen.add(key)
          projectList.push({ id, label: label || `Project #${id}` })
        }
      })
    } else if (row?.projectName || row?.projectId) {
      projectList.push({
        id: row?.projectId || row?.projectName,
        label: row?.projectName || `Project #${row?.projectId}`,
      })
    }

    if (projectList.length === 0) {
      return '-'
    }

    const firstProject = projectList[0]
    const extraCount = projectList.length - 1
    const allLabels = projectList.map((p) => p.label).join(', ')

    return (
      <div
        className="d-flex align-items-center gap-1 flex-nowrap"
        title={allLabels}
      >
        <span
          className="badge"
          style={{
            backgroundColor: '#E8F5E9',
            color: '#2E7D32',
            fontSize: '0.75rem',
            fontWeight: 500,
            padding: '3px 8px',
            borderRadius: '4px',
            display: 'inline-block',
            maxWidth: '140px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={firstProject.label}
        >
          {firstProject.label}
        </span>
        {extraCount > 0 && (
          <span
            className="text-muted"
            style={{
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
            }}
            title={projectList.slice(1).map((p) => p.label).join(', ')}
          >
            +{extraCount} lainnya
          </span>
        )}
      </div>
    )
  }

  const columns = [
    {
      name: <span>Tanggal transaksi</span>,
      cell: (row, index) => (
        <div data-testid={`transaction-table-row-${row?.id ?? index}`}>
          {row?.trxDate
            ? formatDatePMB(row.trxDate, row.trxDate)
            : row?.createdAt
              ? formatDatePMB(row.createdAt, row.createdAt)
              : ''}
        </div>
      ),
      sortable: true,
      sortField: 'createdAt',
      selector: (row) =>
        row?.trxDate
          ? formatDatePMB(row.trxDate, row.trxDate)
          : row?.createdAt
            ? formatDatePMB(row.createdAt, row.createdAt)
            : '',
      minWidth: '150px',
      isFreeze: true,
      freezePosition: 'left',
    },
    {
      name: <span>Nomor transaksi</span>,
      cell: (row) => (
        <div>
          {row?.trxNumber ||
            row?.orderDetail?.transaction_number ||
            row?.orderDetail?.poNumber ||
            '-'}
        </div>
      ),
      selector: (row) =>
        row?.trxNumber ||
        row?.orderDetail?.transaction_number ||
        row?.orderDetail?.poNumber ||
        '-',
      minWidth: '150px',
      isFreeze: true,
      freezePosition: 'left',
    },
    {
      name: <span>{tag?.type === 'SELL' ? 'Pembeli' : 'Penjual'}</span>,
      cell: (row) => {
        const isBuy = tag?.type === 'BUY'

        const detail = isBuy ? row?.sellerDetail : row?.buyerDetail
        const picName =
          detail?.picName ||
          (isBuy ? row?.picSellerDetail?.name : row?.picBuyerDetail?.name)
        const mainName = detail?.name || detail?.fullName
        const displayName = picName || mainName || '-'
        const avatarSrc =
          detail?.profilePhoto ||
          detail?.picProfilePhoto ||
          row?.sellerOrBuyAvatar

        return (
          <div className="d-flex align-items-start gap-2 py-2">
            <ProfilePic
              imgSrc={avatarSrc}
              altText={displayName}
              name={displayName}
              avatarSeed={detail?.userId}
              width={28}
              height={28}
            />

            <div
              className="d-flex flex-column"
              style={{
                minWidth: 0,
                lineHeight: '1.3',
              }}
            >
              <span
                className="fw-medium"
                style={{
                  wordBreak: 'break-word',
                }}
              >
                {displayName}
              </span>

              {detail?.partnership?.name && (
                <div
                  className="d-flex flex-column"
                  style={{ marginTop: '2px' }}
                >
                  <span
                    className="text-primary fw-semibold"
                    style={{
                      fontSize: '0.8rem',
                      wordBreak: 'break-word',
                    }}
                  >
                    {detail.partnership.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        )
      },
      selector: (row) => {
        const isBuy = tag?.type === 'BUY'
        const detail = isBuy ? row?.sellerDetail : row?.buyerDetail
        const picName =
          detail?.picName ||
          (isBuy ? row?.picSellerDetail?.name : row?.picBuyerDetail?.name)
        const mainName = detail?.name || detail?.fullName
        return picName || mainName || '-'
      },
      minWidth: '250px',
    },
    {
      name: <span>Barang</span>,
      cell: (row) => (
        <div className="d-flex flex-column py-2">
          <span className="flex-row">{row?.itemDetail?.[0]?.nameItem}</span>
          <span className="text-primary">
            {intl.formatNumber(row?.itemDetail?.[0]?.amount, {
              useGrouping: 'always',
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}{' '}
            {row?.itemDetail?.[0]?.unit} x Rp
            {intl.formatNumber(row?.itemDetail?.[0]?.price, {
              useGrouping: 'always',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </span>
          <div className="text-muted mt-1 d-flex flex-column">
            <span>
              Kode Inventori:{' '}
              {row?.itemDetail?.[0]?.ownerCode ||
                row?.itemDetail?.[0]?.inventoryOwnerCode ||
                '-'}
            </span>
            <span>Lot ID: {row?.itemDetail?.[0]?.lotCode ?? '-'}</span>
          </div>
        </div>
      ),
      selector: (row) =>
        row?.itemDetail
          ?.map(
            (item) =>
              `${item.nameItem} (${intl.formatNumber(item.amount, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })} ${item.unit} x Rp${intl.formatNumber(item.price, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })})`,
          )
          .join('\n') || '-',
      minWidth: '200px',
    },
    {
      name: <span>Bundle</span>,
      cell: (row) => (
        <div className="d-flex justify-content-end">
          <span>
            {newFormatThousandSeparator(
              row?.unit ?? row?.totalItemDetail ?? 0,
              0,
            )}
          </span>
        </div>
      ),
      right: true,
      sortField: 'totalItemDetail',
      selector: (row) => row?.unit ?? row?.totalItemDetail ?? 0,
      minWidth: '100px',
    },
    {
      name: (
        <div className="d-flex align-items-center gap-1">
          <span>Volume</span>
          <Info size={14} id="volume-tooltip" style={{ cursor: 'pointer', color: '#667085' }} />
          <UncontrolledTooltip placement="top" target="volume-tooltip">
            Total volume dapat memiliki berbagai macam satuan (seperti kg, liter, dll) tergantung pada barang yang ditransaksikan.
          </UncontrolledTooltip>
        </div>
      ),
      cell: (row) => {
        let totalAmount = 0
        let displayUnitStr = ''
        let hasMultipleStr = ''

        const items = row?.itemDetail || []

        if (row?.itemDetailVolume) {
          totalAmount = row.itemDetailVolume.amount || 0
          displayUnitStr = row.itemDetailVolume.unit || ''
          hasMultipleStr = (row.itemDetailVolume.hasOthers === true || String(row.itemDetailVolume.hasOthers).toLowerCase() === 'true') ? '+' : ''
        } else if (row?.qtyByUnit) {
          totalAmount = row.qtyByUnit.total || 0

          const units = Array.from(
            new Set(items.map((i) => i?.unit?.trim()).filter(Boolean)),
          )
          displayUnitStr = units.length > 0 ? units[0] : ''

          hasMultipleStr = units.length > 1 ? '+' : ''
        } else {
          if (items.length === 0) return '-'

          totalAmount = items.reduce(
            (sum, item) => sum + (Number(item?.amount) || 0),
            0,
          )

          const units = Array.from(
            new Set(items.map((i) => i?.unit?.trim()).filter(Boolean)),
          )
          displayUnitStr = units.length > 0 ? units[0] : ''
          hasMultipleStr = units.length > 1 ? '+' : ''
        }

        if (totalAmount === 0) return '-'

        const formattedAmount = newFormatThousandSeparator(totalAmount, 3)
        const finalDisplayUnit = displayUnitStr
          ? ` ${displayUnitStr}${hasMultipleStr}`
          : hasMultipleStr

        return (
          <div className="d-flex justify-content-end">
            <span>
              {formattedAmount}
              {finalDisplayUnit}
            </span>
          </div>
        )
      },
      right: true,
      sortField: 'totalItemDetail',
      selector: (row) => {
        let totalAmount = 0
        let displayUnitStr = ''
        let hasMultipleStr = ''
        const items = row?.itemDetail || []

        if (row?.itemDetailVolume) {
          totalAmount = row.itemDetailVolume.amount || 0
          displayUnitStr = row.itemDetailVolume.unit || ''
          hasMultipleStr = (row.itemDetailVolume.hasOthers === true || String(row.itemDetailVolume.hasOthers).toLowerCase() === 'true') ? '+' : ''
        } else if (row?.qtyByUnit) {
          totalAmount = row.qtyByUnit.total || 0
          const units = Array.from(new Set(items.map((i) => i?.unit?.trim()).filter(Boolean)))
          displayUnitStr = units.length > 0 ? units[0] : ''
          hasMultipleStr = units.length > 1 ? '+' : ''
        } else {
          totalAmount = items.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0)
          const units = Array.from(new Set(items.map((i) => i?.unit?.trim()).filter(Boolean)))
          displayUnitStr = units.length > 0 ? units[0] : ''
          hasMultipleStr = units.length > 1 ? '+' : ''
        }

        if (totalAmount === 0 && items.length === 0) return '-'

        const formattedAmount = newFormatThousandSeparator(totalAmount, 3)
        const finalDisplayUnit = displayUnitStr ? ` ${displayUnitStr}${hasMultipleStr}` : hasMultipleStr

        return `${formattedAmount}${finalDisplayUnit}`
      },
      minWidth: '150px',
    },
    {
      name: (
        <span
          style={{
            textAlign: 'right',
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          Total Transaksi
        </span>
      ),
      cell: (row) => (
        <div className="d-flex justify-content-end" style={{ flex: 0.9 }}>
          <span>Rp{newFormatThousandSeparator(row?.totalAmount, 0)}</span>
        </div>
      ),
      selector: (row) => row?.totalAmount || 0,
      sortable: true,
      sortField: 'totalAmount',
      minWidth: '150px',
    },
    {
      name: <span>Status</span>,
      minWidth: '150px',
      selector: (row) => row?.status,
      cell: (row) => {
        if (row.status?.toLowerCase() === 'selesai') {
          return (
            <div
              className="badge rounded-pill bg-success-50 text-success px-2"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'center',
                maxWidth: '100%',
              }}
            >
              {row.status}
            </div>
          )
        }

        if (row.status?.toLowerCase() === 'menunggu proses penjual') {
          return (
            <div
              className="badge rounded-pill bg-warning-50 text-warning-700 px-2"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'center',
                maxWidth: '100%',
              }}
            >
              {row.status}
            </div>
          )
        }
        if (
          row.status?.toLowerCase() === 'menunggu approval admin' ||
          row.status?.toLowerCase() === 'dalam penyesuaian'
        ) {
          return (
            <div
              className="badge rounded-pill bg-warning-50 text-warning-700 px-2"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'center',
                maxWidth: '100%',
              }}
            >
              {row.status}
            </div>
          )
        }

        if (row.status?.toLowerCase() === 'diproses') {
          return (
            <div
              className="badge rounded-pill bg-primary-50 text-primary px-2"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'center',
                maxWidth: '100%',
              }}
            >
              Diproses oleh Penjual
            </div>
          )
        }

        if (row.status?.toLowerCase() === 'dibatalkan' || row.status?.toLowerCase() === 'expired') {
          return (
            <div
              className="badge rounded-pill bg-danger-50 text-danger-700 px-2"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'center',
                maxWidth: '100%',
              }}
            >
              {row.status}
            </div>
          )
        }
        if (row.status?.toLowerCase() === 'menunggu pembayaran pembeli') {
          return (
            <div
              className="badge rounded-pill bg-warning-50 text-warning-700 px-2"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'center',
                maxWidth: '100%',
              }}
            >
              {row.status}
            </div>
          )
        }

        if (row.status?.toLowerCase() === 'menunggu konfirmasi pembayaran') {
          return (
            <div
              className="badge rounded-pill bg-warning-50 text-warning-700 px-2"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'center',
                maxWidth: '100%',
              }}
            >
              {row.status}
            </div>
          )
        }
      },
    },
    ...(!props?.projectId
      ? [
          {
            name: <span>Project</span>,
            selector: (row) => {
              return renderProjectBadges(row)
            },
            minWidth: '150px',
          },
        ]
      : []),
    {
      name: <span>Metode pembayaran</span>,
      cell: (row) => {
        const method = row?.paymentMethod || getPaymentMethod(row)
        const methodLower = String(method || '').trim().toLowerCase()

        if (
          !method ||
          !methodLower ||
          methodLower === '-' ||
          methodLower === 'tidak diketahui' ||
          methodLower === 'belum ditentukan'
        ) {
          return <span>-</span>
        }

        if (methodLower.includes('cod')) {
          return (
            <div
              className={`badge rounded-pill bg-purple-50 text-purple-700 px-2 py-1 flex-centered gap-1`}
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}
            >
              <SvgIcon
                name="old-icons-hand-holding-usd"
                size={16}
                color="white"
                withBackground={true}
                backgroundColor="#7F56D9"
              />{' '}
              <span>{method}</span>
            </div>
          )
        }

        if (methodLower.includes('kredit')) {
          return (
            <div
              className={`badge rounded-pill bg-primary-50 px-2 py-1 flex-centered gap-1`}
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}
            >
              <SvgIcon
                name="database"
                size={16}
                color="white"
                withBackground={true}
                backgroundColor="#039855"
              />
              <span className="text-black">{method}</span>
            </div>
          )
        }

        if (methodLower.includes('tunai') && !methodLower.includes('non')) {
          return (
            <div
              className="badge rounded-pill bg-gray-100 text-gray-700 px-2 py-1 flex-centered gap-1"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}
            >
              <img width={18} height={18} src={ICTunai} alt={method} /> {method}
            </div>
          )
        }

        if (methodLower.includes('talangan') || methodLower.includes('talangin')) {
          return (
            <div
              className={`badge rounded-pill bg-warning-50 px-2 py-1 flex-centered gap-1`}
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}
            >
              <img width={16} height={16} src={ICTalangin} alt={method} />
              <span className="text-dark fw-bold">{method}</span>
            </div>
          )
        }

        if (methodLower.includes('agen') || methodLower.includes('agent')) {
          return (
            <div
              className="badge rounded-pill text-primary-v2 bg-primary-50 px-2 py-1 flex-centered gap-1"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}
            >
              <User size={14} /> {method}
            </div>
          )
        }

        if (
          methodLower.includes('uang belanja') ||
          methodLower.includes('non tunai')
        ) {
          return (
            <div
              className={`badge rounded-pill bg-primary-50 px-2 py-1 flex-centered gap-1`}
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}
            >
              <img width={16} height={16} src={ICUangBelanja} alt={method} />
              <span className="text-primary">{method}</span>
            </div>
          )
        }

        return (
          <div
            className={`badge rounded-pill bg-primary-50 px-2 py-1 flex-centered gap-1`}
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              maxWidth: '100%',
            }}
          >
            <span className="text-primary">{method}</span>
          </div>
        )
      },
      selector: (row) => row?.paymentMethod || getPaymentMethod(row),
      minWidth: '150px',
    },
    {
      name: <span>Terakhir diperbarui</span>,
      // sortable: true,
      sortField: 'updatedAt',
      selector: (row) =>
        row?.updatedAt ? formatDatePMBV2(row.updatedAt) : '-',
      cell: (row) => {
        return (
          <span>{row?.updatedAt ? formatDatePMB(row.updatedAt) : '-'}</span>
        )
      },
      minWidth: '150px',
    },
    ...(ownerData?.status === 'VERIFIED'
      ? [
        {
          name: '',
          width: '90px',
          selector: (row, index) => {
            const detailId =
              row?.escrowpaymentId ||
              row?.escrowPaymentId ||
              row?.tradeshortcutId ||
              row?.tradeShortcutId ||
              row?.id
            const isArchived = isRowArchived(row)
            const isCompleted =
              String(row?.status || '').toLowerCase() === 'selesai'

            const archiveBtnId = `btn-archive-${row?.id ?? index}`
            const unarchiveBtnId = `btn-unarchive-${row?.id ?? index}`

            return (
              <div className="d-flex align-items-center gap-1 justify-content-center">
                {!hideArchive && (
                  isArchived ? (
                    <>
                      <button
                        id={unarchiveBtnId}
                        type="button"
                        className="btn btn-sm btn-icon p-1 text-muted hover-primary"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleOpenArchiveModal(row, false)
                        }}
                        data-testid={`transaction-table-row-unarchive-${row?.id ?? index}`}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <RotateCcw size={16} color="#475467" />
                      </button>
                      <PMBTooltip
                        target={unarchiveBtnId}
                        placement="top"
                        text="Keluarkan dari Arsip"
                      />
                    </>
                  ) : isCompleted ? (
                    <>
                      <button
                        id={archiveBtnId}
                        type="button"
                        className="btn btn-sm btn-icon p-1 text-muted hover-primary"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleOpenArchiveModal(row, true)
                        }}
                        data-testid={`transaction-table-row-archive-${row?.id ?? index}`}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Archive size={16} color="#475467" />
                      </button>
                      <PMBTooltip
                        target={archiveBtnId}
                        placement="top"
                        text="Arsipkan Transaksi"
                      />
                    </>
                  ) : null
                )}

                <LihatDetail
                  onClick={() => {
                    navigate(
                      `${URL_DETAIL_TRANSACTION}/${detailId}?type=${tag?.type}`,
                      {
                        state: {
                          isList: props?.isList,
                          escrowpaymentId:
                            row?.escrowpaymentId || row?.escrowPaymentId,
                          tradeshortcutId:
                            row?.tradeshortcutId || row?.tradeShortcutId,
                        },
                      },
                    )
                  }}
                  id={detailId}
                  data-testid={`transaction-table-row-detail-${row?.id ?? index}`}
                />
              </div>
            )
          },
          center: true,
          isFreeze: true,
          freezePosition: 'right',
        },
      ]
      : []),
  ]

  const columnsExport = [
    {
      name: <span>Tanggal transaksi</span>,
      selector: (row) =>
        row?.trxDate
          ? formatDatePMBV2(row.trxDate, row.trxDate)
          : row?.createdAt
            ? formatDatePMBV2(row.createdAt, row.createdAt)
            : '-',
      isFreeze: true,
      freezePosition: 'left',
    },
    {
      name: <span>Nomor transaksi</span>,
      selector: (row) =>
        row?.trxNumber ||
        row?.orderDetail?.transaction_number ||
        row?.orderDetail?.poNumber ||
        '-',
      isFreeze: true,
      freezePosition: 'left',
    },
    {
      name: <span>Project</span>,
      selector: (row) => {
        if (Array.isArray(row?.projects) && row.projects.length > 0) {
          const seen = new Set()
          const labels = []
          row.projects.forEach((p) => {
            const label = p?.projectLabel ?? p?.label ?? p?.projectName
            if (label && !seen.has(label)) {
              seen.add(label)
              labels.push(label)
            }
          })
          if (labels.length > 0) return labels.join(', ')
        }
        return row?.projectName || '-'
      },
    },
    {
      name: <span>{tag?.type === 'SELL' ? 'Pembeli' : 'Penjual'}</span>,
      selector: (row) => {
        const isBuy = tag?.type === 'BUY'
        const detail = isBuy ? row?.sellerDetail : row?.buyerDetail
        const picName =
          detail?.picName ||
          (isBuy ? row?.picSellerDetail?.name : row?.picBuyerDetail?.name)
        const mainName = detail?.name || detail?.fullName
        return picName || mainName || '-'
      },
    },
    {
      name: <span>Barang</span>,
      selector: (row) => {
        const items = Array.isArray(row?.itemDetail) ? row.itemDetail : []
        if (items.length > 1) {
          return items.map((i) => i.nameItem).filter(Boolean).join(', ') || '-'
        }
        return items[0]?.nameItem || '-'
      },
    },
    {
      name: <span>Kode Inventori</span>,
      selector: (row) => {
        const items = Array.isArray(row?.itemDetail) ? row.itemDetail : []
        if (items.length > 1) {
          const codes = Array.from(
            new Set(
              items
                .map((i) => i.ownerCode || i.inventoryOwnerCode)
                .filter(Boolean),
            ),
          )
          return codes.length > 0 ? codes.join(', ') : '-'
        }
        return (
          items[0]?.ownerCode ||
          items[0]?.inventoryOwnerCode ||
          '-'
        )
      },
    },
    {
      name: <span>Lot ID</span>,
      selector: (row) => {
        const items = Array.isArray(row?.itemDetail) ? row.itemDetail : []
        if (items.length > 1) {
          const lotCodes = Array.from(
            new Set(items.map((i) => i.lotCode).filter(Boolean)),
          )
          return lotCodes.length > 0 ? lotCodes.join(', ') : '-'
        }
        return items[0]?.lotCode ?? '-'
      },
    },
    {
      name: <span>Satuan</span>,
      selector: (row) => {
        const items = Array.isArray(row?.itemDetail) ? row.itemDetail : []
        if (items.length > 1) {
          const units = Array.from(
            new Set(
              items
                .map((i) => i?.unit?.value ?? i?.unit)
                .filter(Boolean),
            ),
          )
          return (
            row?.itemDetailVolume?.unit ||
            (units.length > 0 ? units.join(', ') : '-')
          )
        }
        return (
          row?.itemDetailVolume?.unit ??
          items[0]?.unit?.value ??
          items[0]?.unit ??
          '-'
        )
      },
    },
    {
      name: <span>Jumlah Bundle</span>,
      selector: (row) => {
        const items = Array.isArray(row?.itemDetail) ? row.itemDetail : []
        if (items.length > 1) {
          return (
            row?.unit ??
            row?.totalItemDetail ??
            items.reduce((acc, curr) => acc + Number(curr?.bundle || 0), 0) ??
            0
          )
        }
        return items[0]?.bundle ?? row?.unit ?? row?.totalItemDetail ?? 0
      },
    },
    {
      name: <span>Jumlah</span>,
      selector: (row) => {
        const items = Array.isArray(row?.itemDetail) ? row.itemDetail : []
        if (items.length > 1) {
          return (
            row?.itemDetailVolume?.amount ??
            items.reduce((acc, curr) => acc + Number(curr?.amount || 0), 0)
          )
        }
        return items[0]?.amount ?? 0
      },
    },
    {
      name: <span>Harga Satuan</span>,
      selector: (row) => {
        const items = Array.isArray(row?.itemDetail) ? row.itemDetail : []
        if (items.length > 1) {
          const prices = Array.from(
            new Set(items.map((i) => Number(i?.price ?? 0))),
          )
          return prices.length > 1 ? 'Bervariasi' : prices[0] ?? 0
        }
        return items[0]?.isDifferentPrices
          ? 'Bervariasi'
          : items[0]?.price ?? 0
      },
    },
    {
      name: (
        <span
          style={{
            textAlign: 'right',
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          Total Transaksi
        </span>
      ),
      selector: (row) => row?.totalAmount || 0,
    },
    {
      name: <span>Metode pembayaran</span>,
      selector: (row) => row?.paymentMethod || getPaymentMethod(row),
    },
    {
      name: <span>Status</span>,
      selector: (row) => row?.status,
    },
    {
      name: <span>Terakhir diperbarui</span>,
      selector: (row) =>
        row?.updatedAt ? formatDatePMBV2(row.updatedAt) : '-',
      isFreeze: true,
      freezePosition: 'right',
    },
  ]

  // const conditionalRowStyles = [
  //   {
  //     when: (row) =>
  //       (row.status?.toLowerCase() === 'diproses' && tag?.type === 'BUY') ||
  //       (row.status?.toLowerCase() === 'menunggu pembayaran pembeli' &&
  //         tag?.type === 'BUY') ||
  //       (row.status?.toLowerCase() === 'menunggu approval admin' &&
  //         ability.can(MANAGE, BUSINESS_MYAPPROVAL) &&
  //         tag?.type === 'BUY' &&
  //         row?.orderDetail?.type === 'BUY') ||
  //       (row.status?.toLowerCase() === 'menunggu approval admin' &&
  //         ability.can(MANAGE, BUSINESS_MYAPPROVAL) &&
  //         tag?.type === 'SELL' &&
  //         row?.orderDetail?.type === 'SELL') ||
  //       (row.status?.toLowerCase() === 'dalam Penyesuaian' &&
  //         tag?.type === 'SELL') ||
  //       (row.status?.toLowerCase() === 'menunggu proses penjual' &&
  //         tag?.type === 'SELL'),
  //     style: {
  //       backgroundColor: '#ecfaff',
  //       '&:hover': {
  //         backgroundColor: '#ecfaff',
  //       },
  //     },
  //   },
  //   {
  //     when: (row) => {
  //       const createdAt = row?.createdAt
  //       if (!createdAt) return false
  //       const diffMs = Date.now() - new Date(createdAt).getTime()
  //       return diffMs >= 0 && diffMs < 60_000
  //     },
  //     classNames: ['row-new-highlight'],
  //   },
  // ]

  const isNewRow = (row) => {
    const createdAt = row?.createdAt
    if (!createdAt) return false
    const diffMs = Date.now() - new Date(createdAt).getTime()
    return diffMs >= 0 && diffMs < 10_000
  }

  const isActiveStatusRow = (row) =>
    (row.status?.toLowerCase() === 'diproses' && tag?.type === 'BUY') ||
    (row.status?.toLowerCase() === 'menunggu pembayaran pembeli' &&
      tag?.type === 'BUY') ||
    (row.status?.toLowerCase() === 'menunggu approval admin' &&
      ability.can(MANAGE, BUSINESS_MYAPPROVAL) &&
      tag?.type === 'BUY' &&
      row?.orderDetail?.type === 'BUY') ||
    (row.status?.toLowerCase() === 'menunggu approval admin' &&
      ability.can(MANAGE, BUSINESS_MYAPPROVAL) &&
      tag?.type === 'SELL' &&
      row?.orderDetail?.type === 'SELL') ||
    (row.status?.toLowerCase() === 'dalam penyesuaian' &&
      tag?.type === 'SELL') ||
    (row.status?.toLowerCase() === 'menunggu proses penjual' &&
      tag?.type === 'SELL')

  const conditionalRowStyles = [
    {
      when: (row) => isActiveStatusRow(row) && !isNewRow(row),
      style: {
        backgroundColor: '#ecfaff',
        '&:hover': { backgroundColor: '#ecfaff' },
      },
    },
    {
      when: (row) => !isActiveStatusRow(row) && isNewRow(row),
      classNames: ['row-new-highlight'],
    },
    {
      when: (row) => isActiveStatusRow(row) && isNewRow(row),
      style: {
        backgroundColor: '#ecfaff',
        '&:hover': { backgroundColor: '#ecfaff' },
      },
      classNames: ['row-new-highlight'],
    },
  ]

  const getPaymentMethod = (row) => {
    if (row?.orderDetail?.usePayable || row?.escrowpayment?.usePayable) {
      return 'Kredit Penjual'
    }
    if (
      row?.orderDetail?.useProofOfpayment ||
      row?.orderDetail?.useProofOfPayment ||
      row?.orderDetail?.paymentTime
    ) {
      return 'COD'
    }
    if (
      row?.orderDetail?.unconfirmed ||
      row?.orderDetail?.useProofOfpayment ||
      row?.orderDetail?.useProofOfPayment ||
      row?.totalAmount === 0
    ) {
      return 'Tunai'
    }
    if (
      row?.sellerDetail?.disbursementViaAgent === true ||
      row?.sellerDetail?.disbursementViaAgent === 'true'
    ) {
      const subMethod =
        tag?.type === 'SELL'
          ? 'Non Tunai'
          : row?.creditRequested
          ? 'Talangin'
          : 'Uang Belanja'
      return `Pembayaran Melalui Agen + ${subMethod}`
    }
    if (row.status?.toLowerCase() === 'menunggu pembayaran pembeli') {
      return '-'
    }
    if (!row?.creditRequested) {
      return tag?.type === 'SELL' ? 'Non Tunai' : 'Uang Belanja'
    }
    if (row?.creditRequested) {
      return tag?.type === 'SELL' ? 'Non Tunai' : 'Talangin'
    }
    return 'Tidak diketahui'
  }

  const getStatusBadge = (status = '') => {
    const s = status.toLowerCase()
    if (s === 'selesai') {
      return (
        <div
          className="badge rounded-pill bg-success-50 text-success px-2 py-1"
          style={{ fontSize: '0.75rem' }}
        >
          {status}
        </div>
      )
    }
    if (
      s === 'menunggu proses penjual' ||
      s === 'menunggu approval admin' ||
      s === 'dalam penyesuaian' ||
      s === 'menunggu pembayaran pembeli' ||
      s === 'menunggu konfirmasi pembayaran'
    ) {
      return (
        <div
          className="badge rounded-pill bg-warning-50 text-warning-700 px-2 py-1"
          style={{ fontSize: '0.75rem' }}
        >
          {status}
        </div>
      )
    }
    if (s === 'diproses') {
      return (
        <div
          className="badge rounded-pill bg-primary-50 text-primary px-2 py-1"
          style={{ fontSize: '0.75rem' }}
        >
          Diproses oleh Penjual
        </div>
      )
    }
    if (s === 'dibatalkan' || s === 'expired') {
      return (
        <div
          className="badge rounded-pill bg-danger-50 text-danger-700 px-2 py-1"
          style={{ fontSize: '0.75rem' }}
        >
          {status}
        </div>
      )
    }
    return (
      <div
        className="badge rounded-pill bg-gray-100 text-gray-700 px-2 py-1"
        style={{ fontSize: '0.75rem' }}
      >
        {status}
      </div>
    )
  }

  const renderMobileCard = (row, index) => {
    const isBuy = tag?.type === 'BUY'
    const counterparty = isBuy ? row?.sellerDetail : row?.buyerDetail
    const picName =
      counterparty?.picName ||
      (isBuy ? row?.picSellerDetail?.name : row?.picBuyerDetail?.name)
    const mainName = counterparty?.name || counterparty?.fullName
    const displayName = picName || mainName || '-'
    const avatarSrc =
      counterparty?.profilePhoto ||
      counterparty?.picProfilePhoto ||
      row?.sellerOrBuyAvatar

    const trxNumber =
      row?.trxNumber ||
      row?.orderDetail?.transaction_number ||
      row?.orderDetail?.poNumber ||
      '-'
    const trxDate = row?.trxDate
      ? formatDatePMB(row.trxDate)
      : row?.createdAt
        ? formatDatePMB(row.createdAt)
        : '-'

    return (
      <Card
        key={index}
        className="mb-3 border-0 shadow-sm"
        style={{ borderRadius: '12px', overflow: 'hidden' }}
        data-testid={`transaction-table-row-${row?.id ?? index}`}
      >
        <CardBody className="p-0">
          {/* Header Section */}
          <div
            className="p-3 border-bottom d-flex justify-content-between align-items-start"
            style={{ backgroundColor: '#F9FAFB' }}
          >
            <div>
              <div
                className="text-primary fw-bold mb-1"
                style={{ fontSize: '0.85rem' }}
              >
                {trxNumber}
              </div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                {trxDate}
              </div>
              {!props?.projectId && renderProjectBadges(row) !== '-' && (
                <div className="mt-1">{renderProjectBadges(row)}</div>
              )}
            </div>
            <div className="text-end">{getStatusBadge(row.status)}</div>
          </div>

          {/* Content Section */}
          <div className="p-3">
            {/* Counterparty */}
            <div className="d-flex align-items-start mb-3">
              <ProfilePic
                imgSrc={avatarSrc}
                altText={displayName}
                name={displayName}
                avatarSeed={counterparty?.userId}
                width={36}
                height={36}
              />
              <div className="ms-2 flex-grow-1">
                <div
                  className="fw-bold text-dark"
                  style={{ fontSize: '0.9rem', lineHeight: '1.4' }}
                >
                  {displayName}
                </div>
                {counterparty?.partnership?.name && (
                  <div
                    className="badge bg-gray-100 text-primary rounded-pill mt-2"
                    style={{ fontSize: '0.7rem', fontWeight: 600 }}
                  >
                    {counterparty.partnership.name}
                  </div>
                )}
              </div>
            </div>

            {/* Commodity */}
            <div
              className="p-2 rounded mb-3"
              style={{
                backgroundColor: '#F9FAFB',
                borderLeft: `3px solid ${colors.primary[500]}`,
              }}
            >
              <div className="fw-medium text-dark small mb-1">
                {row?.itemDetail?.[0]?.nameItem}
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="text-primary fw-bold"
                  style={{ fontSize: '0.8rem' }}
                >
                  {intl.formatNumber(row?.itemDetail?.[0]?.amount, {
                    maximumFractionDigits: 2,
                  })}{' '}
                  {row?.itemDetail?.[0]?.unit}
                </div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                  Rp
                  {newFormatThousandSeparator(row?.itemDetail?.[0]?.price, 0)}
                </div>
              </div>
              {row?.totalItemDetail > 1 && (
                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                  + {row.totalItemDetail - 1} barang lainnya
                </div>
              )}
            </div>

            {/* Meta Info Grid */}
            <div className="row g-2 mb-1">
              <div className="col-6">
                <div
                  className="text-muted mb-1"
                  style={{
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                  }}
                >
                  Metode Pembayaran
                </div>
                <div className="small fw-medium">{getPaymentMethod(row)}</div>
              </div>
              <div className="col-6 text-end">
                <div
                  className="text-muted mb-1"
                  style={{
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                  }}
                >
                  Total Transaksi
                </div>
                <div className="fw-bold text-dark" style={{ fontSize: '1rem' }}>
                  Rp{newFormatThousandSeparator(row?.totalAmount, 0)}
                </div>
              </div>
            </div>
          </div>

          {/* Footer Action */}
          <div
            className="p-2 border-top d-flex align-items-center justify-content-between"
            style={{ backgroundColor: '#F9FAFB' }}
          >
            {!hideArchive && isRowArchived(row) ? (
              <CustomButton
                color="secondary"
                outline
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation()
                  handleOpenArchiveModal(row, false)
                }}
                data-testid={`transaction-mobile-unarchive-${row?.id ?? index}`}
              >
                <RotateCcw size={14} />
                <span>Batal Arsip</span>
              </CustomButton>
            ) : !hideArchive && String(row?.status || '').toLowerCase() === 'selesai' ? (
              <CustomButton
                color="secondary"
                outline
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation()
                  handleOpenArchiveModal(row, true)
                }}
                data-testid={`transaction-mobile-archive-${row?.id ?? index}`}
              >
                <Archive size={14} />
                <span>Arsipkan</span>
              </CustomButton>
            ) : (
              <div />
            )}

            <button
              className="btn btn-link text-decoration-none d-flex align-items-center justify-content-center gap-1 py-1"
              style={{
                color: colors.primary[500],
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
              data-testid={`transaction-table-row-detail-${row?.id ?? index}`}
              onClick={() => {
                const detailId =
                  row?.escrowpaymentId ||
                  row?.escrowPaymentId ||
                  row?.tradeshortcutId ||
                  row?.tradeShortcutId ||
                  row?.id
                navigate(
                  `${URL_DETAIL_TRANSACTION}/${detailId}?type=${tag?.type}`,
                  {
                    state: {
                      isList: props?.isList,
                      escrowpaymentId:
                        row?.escrowpaymentId || row?.escrowPaymentId,
                      tradeshortcutId:
                        row?.tradeshortcutId || row?.tradeShortcutId,
                    },
                  },
                )
              }}
            >
              <span>Lihat Detail</span>
              <ChevronRight size={18} />
            </button>
          </div>
        </CardBody>
      </Card>
    )
  }

  const isInitRef = useRef(true)
  // Ref to always hold the latest getDataTrx to avoid stale closure in setTimeout
  const getDataTrxRef = useRef(getDataTrx)
  getDataTrxRef.current = getDataTrx

  useEffect(() => {
    isInitRef.current = true

    dispatch(
      setFetchOption({
        status: props?.isList ? undefined : 'Aktif',
        from: isCustomDate ? tag?.from : undefined,
        to: isCustomDate ? tag?.to : undefined,
        projectId: props?.projectId || undefined,
      }),
    )
    // Clear client-side filters when navigating between views to avoid stale filter issues
    dispatch(
      setPaginationTag({
        status: undefined,
        payment: undefined,
        trxType: undefined,
        projectId: props?.projectId || undefined,
        selectedContact: undefined,
        searchTerm: '',
        sortby: undefined,
        sortdir: undefined,
        page: 0,
      }),
    )

    // Reset filter refs when navigating between views
    hasLoadedInitialData.current = false
    lastTopIds.current = { BUY: null, SELL: null }
    prevTotalItems.current = { BUY: null, SELL: null }
    prevFilterKey.current = null

    // Use setTimeout so React re-renders with fresh Redux state first,
    // then call getDataTrxRef.current which always points to the latest getDataTrx
    setTimeout(() => {
      isInitRef.current = false
      getDataTrxRef.current()
    }, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.title, props?.projectId])

  useEffect(() => {
    return () => {
      if (props?.projectId) {
        dispatch(
          setPaginationTag({
            projectId: undefined,
          }),
        )
      }
    }
  }, [props?.projectId, dispatch])

  useEffect(() => {
    if (isInitRef.current) return
    if (abilitySell && !abilityBuy) return

    getDataTrx()
  }, [
    abilitySell,
    abilityBuy,
    tag,
    tagPaginationManual,
    isCustomDate,
    isRefetch,
  ])




  return (
    <>
      <SidebarDashboard
        isOpen={isOpenSidebar}
        toggle={toggleSidebar}
        toggleModal={toggle}
        row={selectedRow}
        isOpenModal={isOpen}
        handleCallback={handleCallback}
        options={tag}
      />
      <Card className="border-0 shadow-sm">
        <CardBody className="py-0 px-3 d-flex justify-content-between mt-4 align-items-center">
          <div className="d-flex align-items-center">
            <CardText tag="h4" className="mb-0">
              {props?.title ?? 'Transaksi Aktif'}
            </CardText>
          </div>
          {!props?.hideCreate && <BtnMakeTransaction />}
        </CardBody>
        <CardBody>
          {(abilityBuy || abilitySell) && (
            <DataTable
              selectableRows={props?.isList && !hideArchive}
              selectableRowDisabled={(row) => {
                const isArchived = isRowArchived(row)
                if (isArchived) return false
                return !canRowBeArchived(row)
              }}
              onSelectedRowsChange={({ selectedRows }) => {
                setSelectedTableRows(selectedRows)
              }}
              clearSelectedRows={clearSelectedRows}
              sortServer
              defaultSortFieldId={1}
              defaultSortAsc={false}
              sortIcon={<ChevronUp />}
              onSort={handleSort}
              columns={columns}
              data={paginatedItems}
              progressPending={isLoading || dataTrx?.isLoading || dataTrx?.isFetching}
              pagination
              paginationServer
              paginationComponent={(props) => (
                <PMBPagination
                  rowCount={totalItemsPaginated || 0}
                  currentPage={tagPaginationManual?.page || 0}
                  rowsPerPage={tagPaginationManual?.size || 10}
                  testId="transaction-pagination"
                  setCurrentPage={(e) => {
                    dispatch(
                      setPaginationTag({
                        page: e,
                      }),
                    )
                  }}
                  setRowsPerPage={(e) => {
                    dispatch(
                      setPaginationTag({
                        size: e,
                      }),
                    )
                  }}
                  props={props}
                />
              )}
              subHeader={true}
              subHeaderAlign={'left'}
              subHeaderComponent={
                <HeaderTable
                  abilityBuy={abilityBuy}
                  abilitySell={abilitySell}
                  type={tag?.type}
                  handleFilter={handleFilter}
                  {...props}
                  hideArchive={hideArchive}
                  columns={columnsExport}
                  data={dataTrx}
                  allItems={allItems}
                  isLoading={isLoading || dataTrx?.isLoading || dataTrx?.isFetching}
                  summary={summary}
                />
              }
              conditionalRowStyles={conditionalRowStyles}
              renderMobileCard={renderMobileCard}
            />
          )}
          <div
            style={{
              justifyContent: 'space-between',
              padding: '12px',
              backgroundColor: colors.primary[400],
              color: 'white',
              display: 'flex',
            }}
          >
            <div>
              <P>
                Total:{' '}
                {newFormatThousandSeparator(
                  summary?.totalQty ?? summary?.totalUnitAmount ?? 0,
                  2,
                )}{' '}
                Unit (
                {newFormatThousandSeparator(
                  summary?.totalCommodities ?? summary?.totalItemDetailCount ?? 0,
                  0,
                )}{' '}
                Barang)
              </P>
            </div>
            <div>
              <P>
                Rp{' '}
                {formatNumber(
                  summary?.totalAmount ?? summary?.totalTransaction ?? 0,
                )}
              </P>
            </div>
          </div>
        </CardBody>
      </Card>

      {!hideArchive && selectedTableRows.length > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: '32px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1050,
            backgroundColor: '#1D2939',
            color: '#FFFFFF',
            borderRadius: '10px',
            padding: '12px 20px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
          data-testid="transaction-batch-action-bar"
        >
          <span className="small fw-semibold">
            {selectedTableRows.length} Transaksi Dipilih
          </span>
          <div className="d-flex gap-2">
            <CustomButton
              size="sm"
              outline
              color="secondary"
              onClick={() => {
                setSelectedTableRows([])
                setClearSelectedRows((prev) => !prev)
              }}
              className="text-white border-secondary"
            >
              Batal
            </CustomButton>
            {String(
              tagPaginationManual?.status || tag?.status || '',
            ).toLowerCase() === 'archive' ? (
              <CustomButton
                size="sm"
                color="primary"
                onClick={() => handleOpenArchiveModal(selectedTableRows, false)}
                testId="batch-unarchive-button"
              >
                <RotateCcw size={14} className="me-1" />
                Keluarkan dari Arsip
              </CustomButton>
            ) : (
              <CustomButton
                size="sm"
                color="primary"
                onClick={() => handleOpenArchiveModal(selectedTableRows, true)}
                testId="batch-archive-button"
              >
                <Archive size={14} className="me-1" />
                Arsipkan
              </CustomButton>
            )}
          </div>
        </div>
      )}

      <ModalArchiveTransaction
        isOpen={isModalArchiveOpen}
        toggle={() => setIsModalArchiveOpen((prev) => !prev)}
        handleConfirm={handleConfirmArchive}
        isLoading={isLoadingArchive}
        errMsg={archiveErrMsg}
        isArchive={modalArchiveData.isArchive}
        count={modalArchiveData.rows?.length || 1}
      />
    </>
  )
}

export default TableDashboard
